import { APIv4 as API } from 'src/libs/axios';
import { alertError } from 'src/libs/toastify';
import { OptionFull } from 'src/components/MainPage/_shared/Options';
import { CommonViewProduct } from '../../catalog/MenuPage/services';
import { omit } from 'lodash';
import {
  EntityToBlock,
  UnlockCandidate,
  UnlockCandidates,
} from 'src/pages/catalog/OptionPage/services';
import { PromotionDayMode } from './constants';

export type BaseProductSet = {
  id?: number; // TODO проверить id
  count: number;
  isFree: boolean;
  displayName: string;
  isActive: boolean;
  products: {
    id: number;
    name: string;
    isActive: boolean;
    options: OptionFull[];
    additionalPrice?: number | null;
  }[];
  preselectedProducts?: number[];
};

export type ProductSet = Omit<BaseProductSet, 'products'> & {
  localId: string;
  products: (CommonViewProduct & { additionalPrice: number | null })[];
};

export type BasePromotion = {
  id: number;
  name: string;
  imageName: string;
  isActive: boolean;
  isPickupOnly: boolean;
  goByClick: boolean;
  position: number | null;
  price: number | null;
  discount: number | null;
  showIngredients: boolean;
  productSets: { id: number; count: number }[];
  inaccessibleShops: number[];
};

type Promotion = Omit<BasePromotion, 'productSets' | 'inaccessibleShops'> & {
  description: string;
  goByClick: boolean;
  worksOnHoliday: boolean;
  worksOnPreHoliday: boolean;
  startTime: string;
  startDate: string;
  endTime: string;
  endDate: string;
  dayMode: PromotionDayMode;
  promotionDays: number[];
  productSets: BaseProductSet[];
  catalogs: {
    id: number;
    name: string;
  }[];
  availableShops: {
    id: number;
    name: string;
  }[];
};

export type PromotionValues = Omit<
  Promotion,
  'id' | 'productSets' | 'position'
> & {
  hasDiscount: boolean;
  productSets: ProductSet[];
  unlockCandidates?: UnlockCandidate[];
};

export const fetchPromotions = () =>
  API.get<BasePromotion[]>(`/promotions`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, `Ошибка получения списка акций`));

export const fetchPromotion = ({ id }: { id: number }) =>
  API.get<Promotion>(`/promotions/${id}`)
    .then(({ data }) => ({ ...data, unlockCandidates: [] }))
    .catch((err) => alertError(err, `Ошибка получения акции`));

const adaptPromotion = (values: PromotionValues) => {
  const { productSets, hasDiscount, unlockCandidates, ...rest } = values;
  const catalogs = values.catalogs?.length
    ? values.catalogs.map(({ id }) => id)
    : null;
  const availableShops = values.availableShops?.length
    ? values.availableShops.map(({ id }) => id)
    : [];

  return {
    ...rest,
    catalogs,
    availableShops,
    productSets: !values.goByClick
      ? []
      : productSets.map(({ products, ...rest }) => ({
          ...omit(rest, 'localId', 'isActive'),
          products: products.flatMap(({ variants }) =>
            // @ts-ignore
            variants.map(({ id, additionalPrice }) => ({ id, additionalPrice }))
          ),
        })),
    unlockCandidates: (unlockCandidates ?? []).reduce(
      (candidates, { id, groupKey }) => {
        candidates[groupKey] = candidates[groupKey] || [];
        candidates[groupKey].push(id);

        return candidates;
      },
      {} as UnlockCandidates
    ),
  };
};

export const updateActivity = (params: {
  promotionId: number;
  isActive: boolean;
}) => {
  const { promotionId, isActive } = params;

  return API.get<EntityToBlock[]>(
    `promotions/${promotionId}/bindings?isActive=${!isActive}`
  );
};

export const addPromotion = (data: PromotionValues) =>
  API.post(`/promotions`, adaptPromotion(data)).then(({ data }) =>
    Boolean(data)
  );

export const updatePromotion = (params: {
  id: number;
  data: PromotionValues;
}) =>
  API.put(`/promotions/${params.id}`, adaptPromotion(params.data)).then(
    ({ data }) => Boolean(data)
  );

export const deletePromotion = ({ id }: { id: number }) =>
  API.delete(`/promotions/${id}`).then(({ data }) => data);

export const blockPromotion = (params: { id: number; isActive: boolean }) =>
  API.patch(`/promotions/${params.id}`, { isActive: params.isActive }).then(
    ({ data }) => data
  );
